//
// ============================================================================
// IMPORTS
// ============================================================================
//

import { BaseClient } from "../../clients/base/BaseClient";
import { VISITS_ENDPOINTS } from "./visits.consts";
import type {
  Visit,
  PaginationParams,
  PaginatedResponse,
  VisitResults,
  VisitBalance,
} from "./visits.types";

//
// ============================================================================
// CLIENT
// ============================================================================
//

/**
 * Visits API Client
 *
 * Purpose:
 * Handles all patient visit-related API requests for the LIS Portal.
 *
 * Responsibilities:
 * - Retrieve visits with pagination and filtering
 * - Retrieve visit results
 * - Retrieve visit financial balance
 */
export class VisitsClient extends BaseClient {
  /**
   * Get visits (paginated)
   *
   * Purpose:
   * Retrieve a paged list of visits.
   * Results are always first filtered by the current user's permissions
   * and then by user-defined filter written in FQL.
   *
   * @param params - Pagination parameters (pageNumber, pageSize, filter)
   * @param signal - Optional abort signal for request cancellation
   * @returns Paginated list of visits
   */
  async getVisits(
    params?: PaginationParams,
    signal?: AbortSignal | null,
  ): Promise<PaginatedResponse<Visit>> {
    return this.get<PaginatedResponse<Visit>>(VISITS_ENDPOINTS.LIST, {
      params: {
        pageNumber: params?.pageNumber ?? 1,
        pageSize: params?.pageSize ?? 10,
        filter: params?.filter,
      },
      signal,
    });
  }

  /**
   * Get visit results
   *
   * Purpose:
   * Retrieve a visit results by its identifier.
   * This method returns a visit results object with the patient's information
   * and is available only for users with the right permissions.
   *
   * @param id - Visit ID
   * @param signal - Optional abort signal for request cancellation
   * @returns Visit results with patient info
   */
  async getVisitResults(id: number, signal?: AbortSignal | null): Promise<VisitResults> {
    return this.get<VisitResults>(VISITS_ENDPOINTS.RESULTS(id), { signal });
  }

  /**
   * Get visit balance
   *
   * Purpose:
   * Retrieve a visit balance by its identifier.
   * This method returns a visit financial balance object with the patient's
   * information and is available only for users with the right permissions.
   *
   * @param id - Visit ID
   * @param signal - Optional abort signal for request cancellation
   * @returns Visit balance with patient info
   */
  async getVisitBalance(id: number, signal?: AbortSignal | null): Promise<VisitBalance> {
    return this.get<VisitBalance>(VISITS_ENDPOINTS.BALANCE(id), { signal });
  }

  /**
   * Download visit results as PDF
   *
   * Purpose:
   * Retrieve a PDF blob for the visit's results report.
   *
   * @param id - Visit ID
   * @param signal - Optional abort signal for request cancellation
   * @returns PDF blob
   */
  async downloadPdf(id: number, signal?: AbortSignal | null): Promise<Blob> {
    const url = `${this.baseUrl}${VISITS_ENDPOINTS.PDF(id)}`;
    const headers: Record<string, string> = {};
    if (this.token) headers["Authorization"] = `Bearer ${this.token}`;
    const response = await fetch(url, {
      method: "GET",
      headers,
      signal: signal ?? undefined,
    });
    if (!response.ok) {
      throw new Error(`PDF download failed with status ${response.status}`);
    }
    return response.blob();
  }
}
